import { useNavigate } from "react-router-dom";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import {
  executiveSignals,
  formatCurrency,
  plannedPromotions,
  getRetailerName,
  getSkuInfo,
  retailers,
} from "../../../data/PromotionIQ/PromotionData";
import { AlertTriangle, Clock, TrendingUp, Wallet, ChevronRight, Target, Users } from "lucide-react";
import React from "react";
import { usePromotionSelection } from "../../../components/PromotionIQ/PromotionSelectionContext";

const ExecutiveCommand = () => {
  const navigate = useNavigate();
  const { setSelectedPromotion } = usePromotionSelection();
  const signals = executiveSignals;
  const atRiskPromotions = plannedPromotions.filter(p => p.risk_level === "High");
  
  // Get top performing retailer
  const topRetailer = [...retailers].sort((a, b) => b.roi_index - a.roi_index)[0];

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Executive Command View"
        question="What needs my attention right now?"
      />

      {/* Primary Signals - Investment at Risk Focus */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
        <InteractiveCard
          label="Committed Trade Spend"
          value={formatCurrency(signals.total_trade_spend_committed)}
          subValue="Locked investments"
          variant="default"
          linkTo="/promotioniq/promotion-portfolio"
          linkLabel="View portfolio"
        />
        <InteractiveCard
          label="Flexible Trade Spend"
          value={formatCurrency(signals.total_trade_spend_flexible)}
          subValue="Available to reallocate"
          variant="highlight"
          linkTo="/promotioniq/scenarios"
          linkLabel="Explore scenarios"
        />
        <InteractiveCard
          label="Incremental Revenue (YTD)"
          value={formatCurrency(signals.incremental_revenue_ytd)}
          subValue={`Last cycle: ${formatCurrency(signals.incremental_revenue_last_cycle)}`}
          variant="success"
          linkTo="/promotioniq/retailer-promotion-truth"
          linkLabel="See breakdown"
        />
        <InteractiveCard
          label="Net Margin Delivered"
          value={formatCurrency(signals.net_margin_delivered)}
          subValue="Realized profit"
          variant="success"
          linkTo="/promotioniq/learnings"
          linkLabel="View learnings"
        />
        <InteractiveCard
          label="Promotions at Risk"
          value={signals.promotions_at_risk_count}
          subValue={formatCurrency(signals.promotions_at_risk_value)}
          variant="warning"
          linkTo="/promotioniq/monitoring"
          linkLabel="Take action"
        />
      </div>

      {/* Quick Actions Row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div 
          onClick={() => navigate("/promotioniq/retailer-investment")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-primary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Top Performer: {topRetailer.name}</p>
                <p className="text-sm text-muted-foreground">{topRetailer.roi_index.toFixed(2)}x ROI Index</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        
        <div 
          onClick={() => navigate("/promotioniq/promotion-builder")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-success/10 flex items-center justify-center">
                <Target className="h-5 w-5 text-success" />
              </div>
              <div>
                <p className="font-medium">Design New Investment</p>
                <p className="text-sm text-muted-foreground">Build optimized promotion</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        
        <div 
          onClick={() => navigate("/promotioniq/negotiation")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-warning/10 flex items-center justify-center">
                <Wallet className="h-5 w-5 text-warning" />
              </div>
              <div>
                <p className="font-medium">Negotiation Prep</p>
                <p className="text-sm text-muted-foreground">Data-driven talking points</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>

      {/* Decisions Required Timeline */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5 text-primary" />
          <h3 className="section-header">Decisions Required</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div 
            onClick={() => navigate("/promotioniq/promotion-portfolio")}
            className="text-center p-6 border border-border bg-destructive/10 cursor-pointer hover:border-destructive hover:bg-destructive/20 transition-all group"
          >
            <p className="text-4xl font-bold text-destructive">{signals.decisions_required_30}</p>
            <p className="text-sm text-muted-foreground mt-2">Next 30 Days</p>
            <p className="text-xs text-muted-foreground">Urgent</p>
            <p className="text-xs text-destructive mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to review →</p>
          </div>
          <div 
            onClick={() => navigate("/promotioniq/promotion-portfolio")}
            className="text-center p-6 border border-border bg-warning/10 cursor-pointer hover:border-warning hover:bg-warning/20 transition-all group"
          >
            <p className="text-4xl font-bold text-warning">{signals.decisions_required_60}</p>
            <p className="text-sm text-muted-foreground mt-2">Next 60 Days</p>
            <p className="text-xs text-muted-foreground">Planning</p>
            <p className="text-xs text-warning mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to review →</p>
          </div>
          <div 
            onClick={() => navigate("/promotioniq/promotion-portfolio")}
            className="text-center p-6 border border-border bg-muted/30 cursor-pointer hover:border-primary hover:bg-primary/10 transition-all group"
          >
            <p className="text-4xl font-bold text-foreground">{signals.decisions_required_90}</p>
            <p className="text-sm text-muted-foreground mt-2">Next 90 Days</p>
            <p className="text-xs text-muted-foreground">Horizon</p>
            <p className="text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to review →</p>
          </div>
        </div>
      </div>

      {/* At Risk Promotions */}
      {atRiskPromotions.length > 0 && (
        <div className="border border-warning/50 bg-warning/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              <h3 className="section-header">Investment at Risk</h3>
            </div>
            <button 
              onClick={() => navigate("/promotioniq/monitoring")}
              className="btn-athena text-sm"
            >
              View All Monitoring
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
          <div className="space-y-3">
            {atRiskPromotions.map((promo) => (
              <div 
                key={promo.promotion_id} 
                onClick={() => {
                  setSelectedPromotion(promo.promotion_id);
                  navigate("/promotioniq/promotion-builder");
                }}
                className="flex items-center justify-between p-4 bg-card border border-border cursor-pointer hover:border-warning hover:bg-warning/5 transition-all group"
              >
                <div>
                  <p className="font-medium group-hover:text-warning transition-colors">{promo.promotion_id}</p>
                  <p className="text-sm text-muted-foreground">
                    {getRetailerName(promo.retailer_id)} • {getSkuInfo(promo.sku_id)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-warning">{formatCurrency(promo.planned_trade_spend)}</p>
                  <p className="text-xs text-muted-foreground">at stake</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-destructive">{Math.round(promo.confidence * 100)}% confidence</p>
                  <span className="text-xs bg-destructive/20 text-destructive px-2 py-0.5">{promo.auto_tag}</span>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-warning transition-colors" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Learning & Reallocation Impact */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div 
          onClick={() => navigate("/promotioniq/learnings")}
          className="border border-border bg-card p-6 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              <h3 className="section-header">Investment Reallocated</h3>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <p className="text-3xl font-bold text-primary">{formatCurrency(signals.investment_reallocated)}</p>
          <p className="text-muted-foreground mt-2">Moved from underperforming to high-ROI opportunities</p>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/learnings")}
          className="border border-border bg-card p-6 cursor-pointer hover:border-success hover:bg-success/5 transition-all group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-success" />
              <h3 className="section-header">Learning Impact</h3>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-success transition-colors" />
          </div>
          <p className="text-3xl font-bold text-success">+{Math.round(signals.learning_impact_score * 100)}%</p>
          <p className="text-muted-foreground mt-2">Forecast accuracy improvement from cycle learnings</p>
        </div>
      </div>

      {/* Retailer Performance Quick View */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">Retailer Performance Snapshot</h3>
          <button 
            onClick={() => navigate("/promotioniq/retailer-investment")}
            className="btn-athena text-sm"
          >
            Full Analysis
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {retailers.map((r) => (
            <div 
              key={r.retailer_id} 
              onClick={() => navigate(`/promotioniq/retailer-economics/${r.retailer_id}`)}
              className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-primary/10 hover:border-primary border border-transparent transition-all group"
            >
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">{r.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatCurrency(r.ytd_sales)} YTD • {r.elasticity_sensitivity} elasticity
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className={`text-2xl font-bold ${r.roi_index >= 1.3 ? "text-success" : r.roi_index >= 1 ? "text-warning" : "text-destructive"}`}>
                    {r.roi_index.toFixed(2)}x
                  </p>
                  <p className="text-xs text-muted-foreground">ROI Index</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Key Insight Callout */}
      <div 
        onClick={() => navigate("/promotioniq/summary")}
        className="border border-primary/50 bg-primary/5 p-6 cursor-pointer hover:border-primary hover:bg-primary/10 transition-all group"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Key Insight</p>
            <p className="text-lg">
              This screen orients you around <span className="text-primary font-bold">investment at risk</span>, not activity. 
              Focus on the {signals.promotions_at_risk_count} promotions worth {formatCurrency(signals.promotions_at_risk_value)} that require immediate attention.
            </p>
          </div>
          <div className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-sm">View Executive Summary</span>
            <ChevronRight className="h-5 w-5" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveCommand;
